import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { utils } from '../../utils';
import { apiService } from '../../services';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface ProductivityMetric {
  id: number;
  veiculo: string;
  motorista: string;
  periodo: string;
  quilometragem_percorrida: number;
  horas_trabalhadas: number;
  viagens_realizadas: number;
  consumo_medio: number;
  custo_total: number;
  eficiencia: number;
  status: string;
  data_referencia: string;
}

const Productivity: React.FC = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Hook de infinite scroll
  const {
    items: metrics,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<ProductivityMetric>({
    fetchFunction: async (page, limit, filters) => {
      try {
        const response = await apiService.getProductivityMetrics({
          page,
          limit
        });

        if (response.success && response.data) {
          const apiMetrics = Array.isArray(response.data.metrics) ? response.data.metrics : [];

          const mappedMetrics: ProductivityMetric[] = apiMetrics.map((metric: any) => ({
            id: metric.id,
            veiculo: metric.veiculo || metric.veiculo_placa || 'N/A',
            motorista: metric.motorista || metric.motorista_nome || 'N/A',
            periodo: metric.periodo,
            quilometragem_percorrida: metric.quilometragem_percorrida || 0,
            horas_trabalhadas: metric.horas_trabalhadas || 0,
            viagens_realizadas: metric.viagens_realizadas || 0,
            consumo_medio: metric.consumo_medio || 0,
            custo_total: metric.custo_total || 0,
            eficiencia: metric.eficiencia || 0,
            status: metric.status || 'Regular',
            data_referencia: metric.data_referencia
          }));

          // A paginação está no nível root do response, não dentro de data
          return {
            success: true,
            data: {
              items: mappedMetrics,
              pagination: {
                total: (response as any).pagination?.total || mappedMetrics.length
              }
            }
          };
        }

        throw new Error('Resposta inválida da API');
      } catch (error) {
        console.error('Erro ao carregar métricas de produtividade:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar métricas de produtividade'
        };
      }
    },
    itemsPerPage: 10,
    filters: {},
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Excelente': 'status-success',
      'Bom': 'status-info',
      'Regular': 'status-warning',
      'Ruim': 'status-danger'
    };
    return statusMap[status] || 'status-secondary';
  };

  const getEfficiencyClass = (eficiencia: number): string => {
    if (eficiencia >= 85) return 'status-success';
    if (eficiencia >= 70) return 'status-info';
    if (eficiencia >= 60) return 'status-warning';
    return 'status-danger';
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1
    }).format(value);
  };

  const formatInteger = (value: number): string => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  const filteredMetrics = metrics.filter(metric => {
    const matchesSearch = metric.veiculo.toLowerCase().includes(filter.toLowerCase()) ||
                         metric.motorista.toLowerCase().includes(filter.toLowerCase()) ||
                         metric.periodo.toLowerCase().includes(filter.toLowerCase());
    const matchesVehicle = !vehicleFilter || metric.veiculo === vehicleFilter;
    const matchesPeriod = !periodFilter || metric.periodo === periodFilter;
    const matchesStatus = !statusFilter || metric.status === statusFilter;
    return matchesSearch && matchesVehicle && matchesPeriod && matchesStatus;
  });

  const viewMetric = (id: number) => {
    navigate(`/produtividade/visualizar/${id}`);
  };


  const getVehicles = () => {
    const vehicles = Array.from(new Set(metrics.map(m => m.veiculo)));
    return vehicles;
  };

  const getPeriods = () => {
    const periods = Array.from(new Set(metrics.map(m => m.periodo)));
    return periods;
  };

  const getStatuses = () => {
    const statuses = Array.from(new Set(metrics.map(m => m.status)));
    return statuses;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Produtividade</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-chart-bar"></i>
            Gerar Relatório
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando métricas de produtividade...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Produtividade</h1>
        <button 
          className="btn btn-primary"
          onClick={() => navigate('/produtividade/relatorio')}
        >
          <i className="fas fa-chart-bar"></i>
          Gerar Relatório
        </button>
      </div>

      <div className="page-content">
        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Indicadores de Produtividade</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Veículo, motorista ou período...'
                },
                {
                  type: 'select',
                  label: 'Veículo',
                  value: vehicleFilter,
                  onChange: setVehicleFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getVehicles().map(vehicle => ({ value: vehicle, label: vehicle }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Período',
                  value: periodFilter,
                  onChange: setPeriodFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getPeriods().map(period => ({ value: period, label: period }))
                  ]
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    ...getStatuses().map(status => ({ value: status, label: status }))
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setVehicleFilter('');
                setPeriodFilter('');
                setStatusFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Produtividade"
            />
          </div>

          <div className="card-body">
            {/* Contador de resultados */}
            {filteredMetrics.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="métricas de produtividade"
              />
            )}

            {filteredMetrics.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-chart-bar empty-state-icon"></i>
                <h3>Nenhuma métrica encontrada</h3>
                <p>{filter || vehicleFilter || periodFilter || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Gere o primeiro relatório de produtividade.'}</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/produtividade/relatorio')}
                >
                  Gerar Primeiro Relatório
                </button>
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Período</th>
                      <th>Veículo</th>
                      <th>Motorista</th>
                      <th>Quilometragem</th>
                      <th>Horas</th>
                      <th>Viagens</th>
                      <th>Consumo Médio</th>
                      <th>Custo Total</th>
                      <th>Eficiência</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMetrics.map(metric => (
                      <tr key={metric.id}>
                        <td>
                          <strong>{metric.periodo}</strong>
                        </td>
                        <td>
                          <strong>{metric.veiculo}</strong>
                        </td>
                        <td>{metric.motorista}</td>
                        <td>
                          {formatInteger(metric.quilometragem_percorrida)} km
                        </td>
                        <td>
                          {formatInteger(metric.horas_trabalhadas)} h
                        </td>
                        <td>
                          {formatInteger(metric.viagens_realizadas)}
                        </td>
                        <td>
                          {formatNumber(metric.consumo_medio)} km/L
                        </td>
                        <td>
                          <strong>{formatCurrency(metric.custo_total)}</strong>
                        </td>
                        <td>
                          <span className={`status-badge ${getEfficiencyClass(metric.eficiencia)}`}>
                            {metric.eficiencia}%
                          </span>
                        </td>
                        <td>
                          <span className={`status-badge ${getStatusClass(metric.status)}`}>
                            {metric.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button 
                              className="btn-view"
                              onClick={() => viewMetric(metric.id)}
                              title="Visualizar"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button 
                              className="btn-edit"
                              onClick={() => navigate('/produtividade/editar/' + metric.id)}
                              title="Editar"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Botão "Ver Mais" para carregamento manual */}
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas gerais */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-road"></i>
            </div>
            <div className="stat-value">
              {formatInteger(metrics.reduce((total, m) => total + m.quilometragem_percorrida, 0))}
            </div>
            <div className="stat-label">KM Percorridos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-clock"></i>
            </div>
            <div className="stat-value">
              {formatInteger(metrics.reduce((total, m) => total + m.horas_trabalhadas, 0))}
            </div>
            <div className="stat-label">Horas Trabalhadas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-truck"></i>
            </div>
            <div className="stat-value">
              {formatInteger(metrics.reduce((total, m) => total + m.viagens_realizadas, 0))}
            </div>
            <div className="stat-label">Viagens Realizadas</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-money-bill-wave"></i>
            </div>
            <div className="stat-value">
              {formatCurrency(metrics.reduce((total, m) => total + m.custo_total, 0))}
            </div>
            <div className="stat-label">Custo Total</div>
          </div>
        </div>

        {/* Média de eficiência */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Desempenho por Veículo</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
              {getVehicles().map(vehicle => {
                const vehicleMetrics = metrics.filter(m => m.veiculo === vehicle);
                const totalKm = vehicleMetrics.reduce((total, m) => total + m.quilometragem_percorrida, 0);
                const totalHours = vehicleMetrics.reduce((total, m) => total + m.horas_trabalhadas, 0);
                const totalTrips = vehicleMetrics.reduce((total, m) => total + m.viagens_realizadas, 0);
                const avgEfficiency = vehicleMetrics.reduce((total, m) => total + m.eficiencia, 0) / vehicleMetrics.length;
                const avgConsumption = vehicleMetrics.reduce((total, m) => total + m.consumo_medio, 0) / vehicleMetrics.length;
                
                return (
                  <div key={vehicle} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{vehicle}</span>
                      <span className={`status-badge ${getEfficiencyClass(avgEfficiency)}`}>
                        {formatNumber(avgEfficiency)}%
                      </span>
                    </div>
                    <div style={{ fontSize: '0.9rem', color: '#7f8c8d' }}>
                      <div>KM total: <strong>{formatInteger(totalKm)} km</strong></div>
                      <div>Horas: <strong>{formatInteger(totalHours)} h</strong></div>
                      <div>Viagens: <strong>{formatInteger(totalTrips)}</strong></div>
                      <div>Consumo médio: <strong>{formatNumber(avgConsumption)} km/L</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Distribuição por status */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Distribuição por Status</h3>
          </div>
          <div className="card-body">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
              {getStatuses().map(status => {
                const statusMetrics = metrics.filter(m => m.status === status);
                const percentage = Math.round((statusMetrics.length / metrics.length) * 100);
                
                return (
                  <div key={status} style={{ padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '600', color: '#2c3e50' }}>{status}</span>
                      <span style={{ color: '#04C64F', fontWeight: 'bold' }}>{statusMetrics.length}</span>
                    </div>
                    <div style={{ background: '#e9ecef', borderRadius: '4px', height: '8px' }}>
                      <div 
                        style={{ 
                          background: 'linear-gradient(135deg, #04C64F, #03a542)', 
                          height: '100%', 
                          borderRadius: '4px', 
                          width: `${percentage}%` 
                        }}
                      ></div>
                    </div>
                    <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#7f8c8d' }}>
                      <div>Percentual: <strong>{percentage}%</strong></div>
                      <div>KM médio: <strong>{formatInteger(statusMetrics.reduce((total, m) => total + m.quilometragem_percorrida, 0) / statusMetrics.length)} km</strong></div>
                      <div>Eficiência média: <strong>{formatNumber(statusMetrics.reduce((total, m) => total + m.eficiencia, 0) / statusMetrics.length)}%</strong></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Gráfico de tendência */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h3>Tendência de Eficiência</h3>
          </div>
          <div className="card-body">
            <div style={{ textAlign: 'center', padding: '40px 20px', background: '#f8f9fa', borderRadius: '8px' }}>
              <i className="fas fa-chart-line" style={{ fontSize: '3rem', color: '#04C64F', marginBottom: '15px' }}></i>
              <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>Gráfico de Tendência</h4>
              <p style={{ color: '#7f8c8d', fontSize: '0.9rem' }}>
                O gráfico de tendência de eficiência será exibido aqui após a integração com a API.
              </p>
              <button 
                className="btn btn-primary"
                style={{ marginTop: '15px' }}
                onClick={() => navigate('/produtividade/graficos')}
              >
                <i className="fas fa-chart-line"></i>
                Ver Gráficos Detalhados
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Productivity;
